// The route tree (ADR 0023): /login, and the signed-in pages inside the app shell. Pages
// whose views haven't been built show a placeholder body. An admin views as a user under
// /view-as/$userId: the same rep, supervisor and pay viewer pages (routes/payRoutes.tsx,
// routes/peopleRoutes.tsx), with that user's clients, nav and banner (ADR 0017's A19 and
// A20 amendments).
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  Outlet,
  redirect,
  useLocation,
  useNavigate,
  useRouteContext,
  type RouterHistory,
} from '@tanstack/react-router';
import {
  ErrorPage,
  ImpersonationBanner,
  resolveBrandConfig,
  ThemeRoot,
  ToastProvider,
  useToast,
  type BrandPack,
} from '@hkdevs/design-system';
import { useEffect, useMemo, type ReactNode } from 'react';
import { ApiProvider, useApiClient } from './api/ApiProvider';
import { ApiError, unwrap, type ApiClient } from './api/client';
import { useStopViewing } from './api/queries/impersonation';
import { queryKeys } from './api/queries/keys';
import { meQuery, useMe } from './api/queries/me';
import { useNavCounts } from './api/queries/navCounts';
import { tenantQuery, useTenant } from './api/queries/tenant';
import { createViewAsClients, type ViewAsClients } from './api/viewAs';
import { withReference } from './api/withReference';
import { Login, type LoginError } from './routes/Login';
import { payRoutes } from './routes/payRoutes';
import { peopleRoutes } from './routes/peopleRoutes';
import { InShellError, InShellNotFound, PageGate, UUID } from './shell/gates';
import { NotFound, PageSkeleton, UnexpectedError } from './shell/Page';
import { navItemAt } from './shell/pages';
import { SignedInShell } from './shell/SignedInShell';
import { useUserNotices } from './shell/UserNotices';
import { useViewAs, withoutViewAs, withViewAs } from './shell/viewAs';
import { tenantBrand } from './theme/brand';
import { parseSearch, stringifySearch } from './urlDates';
import { NoticesPage } from './views/notices/NoticesView';
import { PayHistoryUploadsPage } from './views/payData/PayHistoryUploadsPage';
import { parsePayDataHistorySearch } from './views/payData/PayHistoryUploadsView';
import { PendingPayUploadsPage } from './views/payData/PendingPayUploadsPage';
import { parsePayDataPendingSearch } from './views/payData/PendingPayUploadsView';
import { ProductsPage } from './views/products/ProductsPage';
import { ProductTypePage } from './views/products/ProductTypePage';
import { SettingsPage } from './views/settings/SettingsPage';
import { OperatorView } from './views/operator/OperatorView';
import { UsersPage } from './views/users/UsersPage';
import { parseUsersSearch } from './views/users/UsersView';

export interface RouterContext {
  queryClient: QueryClient;
  api: ApiClient;
  /** Leaves the app for a URL outside the router (the provider's sign-in). */
  openExternal: (url: string) => void;
  /** The deployment's brand pack (loadDeploymentPack). */
  brandPack: BrandPack;
}

/** The context the routes see: the app's, plus the clients for viewing as a user. */
interface AppContext extends RouterContext {
  viewAsClients: ViewAsClients;
}

/** Themes the document with the deployment's pack and the tenant's values. */
function Root() {
  const { brandPack } = useRouteContext({ from: '__root__' });
  const { data: tenant } = useTenant();
  const brand = useMemo(
    () => resolveBrandConfig(brandPack, tenant ? tenantBrand(tenant) : {}),
    [brandPack, tenant],
  );
  return (
    <ThemeRoot brand={brand} target="document">
      <ToastProvider>
        <Outlet />
      </ToastProvider>
    </ThemeRoot>
  );
}

const rootRoute = createRootRouteWithContext<AppContext>()({
  // The tenant is loaded before the first render, so the brand is complete when it paints.
  beforeLoad: async ({ context }) => {
    await context.queryClient.ensureQueryData(tenantQuery(context.api));
  },
  component: Root,
  // Signed-in paths that match no page reach the splat route below, inside the shell.
  notFoundComponent: () => <NotFound home="/" />,
  errorComponent: ({ error }) => <UnexpectedError error={error} standalone />,
});

const LOGIN_ERRORS: readonly LoginError[] = ['not_authorized', 'provider_error', 'link_expired'];

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  // Always return the key: the router merges parent search params, so an omitted key
  // would let an unvalidated ?error= through.
  validateSearch: (search: Record<string, unknown>): { error?: LoginError | undefined } => ({
    error: LOGIN_ERRORS.includes(search.error as LoginError)
      ? (search.error as LoginError)
      : undefined,
  }),
  component: function LoginRoute() {
    const { data: tenant } = useTenant();
    const { error } = loginRoute.useSearch();
    const { openExternal } = useRouteContext({ from: '__root__' });
    if (!tenant) return null;
    return <Login tenant={tenant} error={error} onSignIn={openExternal} />;
  },
});

/**
 * The app shell for the identity of the clients around it: the signed-in user's, or under
 * /view-as/ the viewed user's (with the banner).
 */
function Shell({ banner, children }: { banner?: ReactNode; children: ReactNode }) {
  const api = useApiClient();
  const queryClient = useQueryClient();
  const { queryClient: ownQueries } = useRouteContext({ from: '__root__' });
  const navigate = useNavigate();
  const { toast } = useToast();
  const viewing = useViewAs() !== null;
  const pathname = useLocation({ select: (location) => location.pathname });
  const { data: tenant } = useTenant();
  const { data: me } = useMe();
  const { data: counts } = useNavCounts();
  const notices = useUserNotices(!!tenant?.features.includes('notices') && !!me && !viewing);
  const signOut = useMutation({
    mutationFn: () => unwrap(api.POST('/api/auth/logout')),
    onSuccess: async () => {
      api.setCsrfToken(null);
      queryClient.removeQueries({ queryKey: queryKeys.me() });
      ownQueries.removeQueries({ queryKey: queryKeys.me() });
      await navigate({ to: '/login' });
    },
    onError: (error) => {
      toast(
        withReference(
          {
            tone: 'danger',
            title: "Couldn't sign you out",
            description: 'Check your connection and try again.',
          },
          error,
        ),
      );
    },
  });
  if (!tenant || !me) return null;
  return (
    <SignedInShell
      me={me}
      features={tenant.features}
      counts={counts}
      active={navItemAt(withoutViewAs(pathname))}
      onSignOut={() => signOut.mutate()}
      banner={banner ?? notices.banner}
    >
      {children}
      {notices.modal}
    </SignedInShell>
  );
}

const signedInRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'signed-in',
  beforeLoad: async ({ context }) => {
    try {
      return { me: await context.queryClient.ensureQueryData(meQuery(context.api)) };
    } catch (error) {
      if (error instanceof ApiError && error.code === 'unauthenticated') {
        throw redirect({ to: '/login' });
      }
      throw error;
    }
  },
  // /api/me failed: there's no user to build the shell for.
  errorComponent: ({ error }) => <UnexpectedError error={error} standalone />,
  component: function SignedInLayout() {
    // Under /view-as/ the viewed user's layout draws the shell.
    return useViewAs() ? (
      <Outlet />
    ) : (
      <Shell>
        <Outlet />
      </Shell>
    );
  },
});

const indexRoute = createRoute({
  getParentRoute: () => signedInRoute,
  path: '/',
  beforeLoad: ({ context }) => {
    throw redirect({ href: context.me.defaultRoute });
  },
});

/**
 * An admin viewing as a user, in this tab (ADR 0017's A19 amendment): the user's own
 * pages, nav and banner, from clients that send X-View-As and keep their own cache. The
 * first request starts the viewing; a refusal (the API's 403, 404 or 409) says so.
 */
const viewAsRoute = createRoute({
  getParentRoute: () => signedInRoute,
  path: '/view-as/$userId',
  errorComponent: InShellError,
  component: function ViewAsLayout() {
    const { userId } = viewAsRoute.useParams();
    const { viewAsClients } = useRouteContext({ from: '__root__' });
    const { api, queryClient } = viewAsClients(userId);
    return (
      <ApiProvider key={userId} client={api} queryClient={queryClient}>
        <ViewAsShell />
      </ApiProvider>
    );
  },
});

/** The viewed user's shell, once their /api/me (the first request) has answered. */
function ViewAsShell() {
  const { me: own } = viewAsRoute.useRouteContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const me = useMe();
  const stop = useStopViewing();
  if (me.error instanceof ApiError && [403, 404, 409].includes(me.error.status)) {
    return (
      <ErrorPage
        variant="forbidden"
        title="You can’t view as this user"
        description="Only admins can view as someone, and never as another admin."
        homeHref={own.defaultRoute}
        standalone
      />
    );
  }
  if (me.error) return <UnexpectedError error={me.error} home={own.defaultRoute} standalone />;
  if (!me.data) return <PageSkeleton />;
  const exit = () =>
    stop.mutate(undefined, {
      // The real user's landing, without the prefix; then forget this identity's data.
      onSuccess: (real) =>
        void navigate({ href: real.defaultRoute }).then(() => queryClient.clear()),
      onError: (error) =>
        toast(
          withReference(
            {
              tone: 'danger',
              title: 'Couldn’t go back to the user list',
              description: 'Check your connection and try again.',
            },
            error,
          ),
        ),
    });
  return (
    <Shell banner={<ImpersonationBanner userName={me.data.user.name} onExit={exit} />}>
      <Outlet />
    </Shell>
  );
}

/** /view-as/$userId itself: the viewed user's landing. */
const viewAsIndexRoute = createRoute({
  getParentRoute: () => viewAsRoute,
  path: '/',
  component: function ViewAsIndex() {
    const { userId } = viewAsRoute.useParams();
    const { data: me } = useMe();
    const navigate = useNavigate();
    useEffect(() => {
      if (me) void navigate({ href: withViewAs(me.defaultRoute, userId), replace: true });
    }, [me, navigate, userId]);
    return null;
  },
});

/** Any other path under /view-as/: the 404 page, in the viewed user's shell. */
const viewAsNotFoundRoute = createRoute({
  getParentRoute: () => viewAsRoute,
  path: '$',
  component: InShellNotFound,
});

/** The admin's Users page: search, role and page in the URL. */
const usersRoute = createRoute({
  getParentRoute: () => signedInRoute,
  path: '/users',
  validateSearch: parseUsersSearch,
  errorComponent: InShellError,
  component: function UsersRoute() {
    const navigate = usersRoute.useNavigate();
    return (
      <PageGate page="users">
        <UsersPage
          search={usersRoute.useSearch()}
          onSearchChange={(next) => void navigate({ search: next, replace: true })}
        />
      </PageGate>
    );
  },
});

/** The admin's site notices (pc-dbb.18). */
const noticesRoute = createRoute({
  getParentRoute: () => signedInRoute,
  path: '/notices',
  errorComponent: InShellError,
  component: () => (
    <PageGate page="notices">
      <NoticesPage />
    </PageGate>
  ),
});

/** The admin's Pay history uploads: the view, filters and page in the URL. */
const payDataHistoryRoute = createRoute({
  getParentRoute: () => signedInRoute,
  path: '/pay-data/history',
  validateSearch: parsePayDataHistorySearch,
  errorComponent: InShellError,
  component: function PayDataHistoryRoute() {
    const navigate = payDataHistoryRoute.useNavigate();
    return (
      <PageGate page="payDataHistory">
        <PayHistoryUploadsPage
          search={payDataHistoryRoute.useSearch()}
          onSearchChange={(next) => void navigate({ search: next, replace: true })}
        />
      </PageGate>
    );
  },
});

/** The admin's Pending pay uploads: the search, status and page in the URL. */
const payDataPendingRoute = createRoute({
  getParentRoute: () => signedInRoute,
  path: '/pay-data/pending',
  validateSearch: parsePayDataPendingSearch,
  errorComponent: InShellError,
  component: function PayDataPendingRoute() {
    const navigate = payDataPendingRoute.useNavigate();
    return (
      <PageGate page="payDataPending">
        <PendingPayUploadsPage
          search={payDataPendingRoute.useSearch()}
          onSearchChange={(next) => void navigate({ search: next, replace: true })}
        />
      </PageGate>
    );
  },
});

/** The admin's Settings: the white-label configuration and external ID types. */
const settingsRoute = createRoute({
  getParentRoute: () => signedInRoute,
  path: '/settings',
  errorComponent: InShellError,
  component: () => (
    <PageGate page="settings">
      <SettingsPage />
    </PageGate>
  ),
});

/** The operator dashboard (ADR 0016's A25 amendment): 404 for anyone but an operator. */
const operatorRoute = createRoute({
  getParentRoute: () => signedInRoute,
  path: '/operator',
  errorComponent: InShellError,
  component: () => (
    <PageGate page="operator">
      <OperatorView />
    </PageGate>
  ),
});

/** The admin's product types. */
const productsRoute = createRoute({
  getParentRoute: () => signedInRoute,
  path: '/products',
  errorComponent: InShellError,
  component: function ProductsRoute() {
    const navigate = productsRoute.useNavigate();
    return (
      <PageGate page="products">
        <ProductsPage
          onOpen={(productId) =>
            void navigate({ to: '/products/$productId', params: { productId } })
          }
        />
      </PageGate>
    );
  },
});

/** One product type's fields. */
const productRoute = createRoute({
  getParentRoute: () => signedInRoute,
  path: '/products/$productId',
  errorComponent: InShellError,
  component: function ProductRoute() {
    const { productId } = productRoute.useParams();
    if (!UUID.test(productId)) return <InShellNotFound />;
    return (
      <PageGate page="products">
        <ProductTypePage key={productId} productId={productId} />
      </PageGate>
    );
  },
});

const pageRoutes = [
  ...payRoutes(signedInRoute),
  ...peopleRoutes(signedInRoute),
  usersRoute,
  noticesRoute,
  payDataHistoryRoute,
  payDataPendingRoute,
  productsRoute,
  productRoute,
  settingsRoute,
  operatorRoute,
];

/** Any other signed-in path: the 404 page, inside the shell. */
const notFoundRoute = createRoute({
  getParentRoute: () => signedInRoute,
  path: '$',
  component: InShellNotFound,
});

const routeTree = rootRoute.addChildren([
  loginRoute,
  signedInRoute.addChildren([
    indexRoute,
    ...pageRoutes,
    // The rep, supervisor and pay viewer pages again, as the viewed user (A19, A20); no
    // admin pages.
    viewAsRoute.addChildren([
      viewAsIndexRoute,
      ...payRoutes(viewAsRoute),
      ...peopleRoutes(viewAsRoute),
      viewAsNotFoundRoute,
    ]),
    notFoundRoute,
  ]),
]);

export function createAppRouter(context: RouterContext, history?: RouterHistory) {
  // A 401 from a viewed user's request means the session ended, as from any other.
  let goToLogin = () => {};
  const viewAsClients = createViewAsClients(() => goToLogin());
  const router = createRouter({
    routeTree,
    context: { ...context, viewAsClients },
    ...(history ? { history } : {}),
    defaultPreload: false,
    // Date params are MM-DD-YYYY in the URL and ISO inside the app (urlDates.ts).
    parseSearch,
    stringifySearch,
    // Shown when a route takes longer than this to load, the first load included (the
    // router uses the default there, not the child route's).
    defaultPendingComponent: PageSkeleton,
    defaultPendingMs: 300,
  });
  goToLogin = () => {
    if (router.state.location.pathname !== '/login') void router.navigate({ to: '/login' });
  };
  return router;
}

export type AppRouter = ReturnType<typeof createAppRouter>;

declare module '@tanstack/react-router' {
  interface Register {
    router: AppRouter;
  }
}
